/**
 * URL Utility Functions
 * Helper functions for URL processing and validation
 */

/**
 * Strip protocol and path from URL (show only domain)
 * Removes http://, https://, www., paths, query params, and trailing slashes
 * 
 * @param {string} url - The URL to clean
 * @returns {string} - Clean domain name (e.g., "example.com")
 * 
 * @example
 * stripProtocol("https://www.example.com/path?query=1") // "example.com"
 * stripProtocol("http://example.com/") // "example.com"
 */
export const stripProtocol = (url) => {
    if (!url) return '';
    
    try {
        // Remove any protocol
        let domain = url.replace(/^https?:\/\//i, '');
        // Remove www. prefix
        domain = domain.replace(/^www\./, '');
        // Extract just the domain part (before first /)
        domain = domain.split('/')[0];
        // Remove any trailing slashes or query params
        domain = domain.split('?')[0];
        // Remove trailing slash
        domain = domain.replace(/\/$/, '');
        return domain;
    } catch (e) {
        console.error('Error stripping protocol:', e);
        return url;
    }
};

/**
 * Validate URL/domain format
 * Checks if the URL is valid and follows domain naming conventions
 * 
 * @param {string} urlValue - The URL to validate
 * @returns {{isValid: boolean, error: string|null}} - Validation result with error message
 * 
 * @example
 * validateUrl("example.com") // {isValid: true, error: null}
 * validateUrl("") // {isValid: false, error: "URL is required"}
 * validateUrl("invalid..domain") // {isValid: false, error: "Invalid domain format"}
 */
export const validateUrl = (urlValue) => {
    const trimmedUrl = urlValue.trim();
    
    if (!trimmedUrl) {
        return {
            isValid: false,
            error: 'URL is required'
        };
    }
    
    // Strip protocol and clean the URL
    const cleanUrl = stripProtocol(trimmedUrl);
    
    if (!cleanUrl) {
        return {
            isValid: false,
            error: 'Invalid URL format'
        };
    }
    
    // Check for invalid patterns
    // No consecutive dots
    if (cleanUrl.includes('..')) {
        return {
            isValid: false,
            error: 'Invalid domain format'
        };
    }
    
    // No leading or trailing dots
    if (cleanUrl.startsWith('.') || cleanUrl.endsWith('.')) {
        return {
            isValid: false,
            error: 'Invalid domain format'
        };
    }
    
    // Split domain into parts
    const parts = cleanUrl.split('.');
    
    // Must have at least 2 parts (domain + TLD)
    if (parts.length < 2) {
        return {
            isValid: false,
            error: 'Invalid domain format'
        };
    }
    
    // Maximum 5 parts (sub.sub.sub.domain.tld) - real-world limit
    if (parts.length > 5) {
        return {
            isValid: false,
            error: 'Invalid domain format'
        };
    }
    
    // TLD (last part) must be 2-63 characters and only letters
    const tld = parts[parts.length - 1];
    if (!/^[a-zA-Z]{2,63}$/.test(tld)) {
        return {
            isValid: false,
            error: 'Invalid domain format'
        };
    }
    
    // Check each part (domain and subdomains)
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        
        // Each part must be 1-63 characters (RFC 1035)
        if (part.length < 1 || part.length > 63) {
            return {
                isValid: false,
                error: 'Invalid domain format'
            };
        }
        
        // Each part must start and end with alphanumeric
        if (!/^[a-zA-Z0-9]/.test(part) || !/[a-zA-Z0-9]$/.test(part)) {
            return {
                isValid: false,
                error: 'Invalid domain format'
            };
        }
        
        // Each part can contain alphanumeric and hyphens
        if (!/^[a-zA-Z0-9-]+$/.test(part)) {
            return {
                isValid: false,
                error: 'Invalid domain format'
            };
        }
    }
    
    // Check for invalid patterns like .com.com.com (repeated TLD-like patterns)
    // If last 2 parts are same, it's likely invalid (e.g., com.com, org.org)
    if (parts.length >= 2 && parts[parts.length - 1] === parts[parts.length - 2]) {
        return {
            isValid: false,
            error: 'Invalid domain format'
        };
    }
    
    // Logical check: If any consecutive parts are identical and short (2-4 chars),
    // it's likely an invalid repeated TLD-like pattern (e.g., com.com, org.org, co.co)
    // This catches patterns like hello.com.com.com.co
    for (let i = 0; i < parts.length - 1; i++) {
        const currentPart = parts[i];
        const nextPart = parts[i + 1];
        
        // If consecutive parts are identical and both are short (2-4 chars),
        // it's an invalid repeated pattern (TLD-like parts shouldn't repeat)
        if (currentPart === nextPart && currentPart.length >= 2 && currentPart.length <= 4) {
            return {
                isValid: false,
                error: 'Invalid domain format'
            };
        }
    }
    
    return {
        isValid: true,
        error: null
    };
};

